<script setup lang="ts">
import { computed, onBeforeUnmount, provide, watch } from "vue";
import { useRoute } from "vue-router";
import type { BreadcrumbItem } from "@/components/Core/BreadcrumbBar.vue";
import PageTitleBlock from "@/components/Shell/PageTitleBlock.vue";
import SidebarToggle from "@/components/Shell/SidebarToggle.vue";
import SidepanelToggle from "@/components/Shell/SidepanelToggle.vue";
import { useShellVisibility } from "@/composables/useShellVisibility";
import {
  pageFrameBreadcrumbsKey,
  pageFrameTitleKey,
  usePageShellState,
} from "@/composables/usePageShell";
import { useRouteTitle } from "@/composables/useRouteTitle";
import FeatureGate from "@/components/Core/Paywall/FeatureGate.vue";
import { routePaywallFlag } from "@/services/feature_gate";

const CONTENT_WIDTH_CLASSES = {
  standard: "mx-auto w-full max-w-7xl px-4 sm:px-5 lg:px-10",
  wide: "w-full px-4 sm:px-5 lg:px-10",
  "full-bleed": "w-full",
} as const;

const TITLE_HEIGHT_PX = 52;

const route = useRoute();
const registration = usePageShellState();
const { hasApplicationHeader, hasContentFrame } = useShellVisibility();
const routeTitle = useRouteTitle();
const title = computed(
  () => registration.title.value?.trim() || routeTitle.value,
);
provide(pageFrameTitleKey, title);
// Walk the matched chain, not just `route.meta`: a gated parent (e.g. the
// Competitor Monitor shell) must paywall its children too, and the leaf's own
// merged meta isn't guaranteed to carry the parent's flag.
const paywallFlag = computed(() => {
  // Walk leaf-first so a child can opt out of an ancestor's paywall with
  // `paywall: false` — `mcp-documentation` is nested under the paywalled MCP
  // shell for its breadcrumbs but must render the guide, not the pitch.
  for (const record of [...route.matched].reverse()) {
    if (record.meta.paywall === false) return undefined;
    // A view that gates itself keeps its own chrome, so an ancestor must not
    // paywall it either — otherwise PageFrame would replace the very view that
    // was told to render the pitch inside its tab bar.
    if (record.meta.paywall === "in-component") return undefined;
    const flag = routePaywallFlag(record.meta);
    if (flag) return flag;
  }
  return undefined;
});

const contentWidth = computed(() => route.meta.contentWidth ?? "standard");
const contentClasses = computed(
  () => CONTENT_WIDTH_CLASSES[contentWidth.value],
);

const routeBreadcrumbs = computed<BreadcrumbItem[]>(() => {
  const terminalDeclaration = route.matched.at(-1)?.meta.breadcrumb;
  if (
    terminalDeclaration === false &&
    registration.extraBreadcrumbs.value.length === 0
  ) {
    return [];
  }
  const crumbs: BreadcrumbItem[] = route.matched.flatMap((record) => {
    const declaration = record.meta.breadcrumb;
    if (!declaration) return [];
    const dynamicLabel = registration.breadcrumbLabel.value?.trim();
    return [
      {
        label: declaration.dynamic
          ? dynamicLabel || declaration.fallback || declaration.label
          : declaration.label,
        to:
          registration.breadcrumbTos.value[String(record.name)] ??
          declaration.to,
      },
    ];
  });
  if (registration.extraBreadcrumbs.value.length === 0) return crumbs;
  const insertionIndex =
    typeof terminalDeclaration === "object"
      ? Math.max(crumbs.length - 1, 0)
      : crumbs.length;
  return [
    ...crumbs.slice(0, insertionIndex),
    ...registration.extraBreadcrumbs.value,
    ...crumbs.slice(insertionIndex),
  ];
});
const visibleBreadcrumbs = computed(() => {
  const breadcrumbs = routeBreadcrumbs.value;
  const lastLabel = breadcrumbs.at(-1)?.label?.trim();
  const duplicatesTitle =
    lastLabel === title.value?.trim() || lastLabel === route.meta.title;
  return duplicatesTitle ? breadcrumbs.slice(0, -1) : breadcrumbs;
});
provide(pageFrameBreadcrumbsKey, visibleBreadcrumbs);

const titleTop = computed(() =>
  hasApplicationHeader.value ? "var(--mobile-navbar-height, 0px)" : "0px",
);
const pageFrameBottom = computed(() => {
  if (!hasApplicationHeader.value) return "var(--mobile-navbar-height, 0px)";
  return `calc(var(--mobile-navbar-height, 0px) + ${TITLE_HEIGHT_PX}px)`;
});
const headerStyle = computed(() => ({
  top: "var(--mobile-navbar-height, 0px)",
  left: "var(--sidebar-width, 0px)",
  right: "var(--sidepanel-width, 0px)",
}));

watch(
  [titleTop, pageFrameBottom],
  ([top, bottom]) => {
    document.documentElement.style.setProperty("--page-frame-title-top", top);
    document.documentElement.style.setProperty("--page-frame-bottom", bottom);
  },
  { immediate: true },
);
onBeforeUnmount(() => {
  document.documentElement.style.setProperty("--page-frame-title-top", "0px");
  document.documentElement.style.setProperty("--page-frame-bottom", "0px");
});
</script>

<template>
  <div v-if="hasContentFrame" class="min-w-0">
    <header
      v-if="hasApplicationHeader"
      data-page-frame-header
      class="fixed z-40 bg-white"
      :style="headerStyle"
    >
      <div
        class="flex h-13 items-stretch border-b border-gray-200 px-4 sm:px-5 lg:px-10"
      >
        <div data-page-frame-control class="flex lg:-ml-10">
          <SidebarToggle class="mr-3" />
        </div>
        <PageTitleBlock
          data-page-frame-title
          :title="title"
          :crumbs="visibleBreadcrumbs"
        />
        <div data-page-frame-control class="flex lg:-mr-10">
          <SidepanelToggle />
        </div>
      </div>
    </header>
    <main
      :class="contentClasses"
      :data-content-width="contentWidth"
      :style="{ paddingTop: 'var(--page-frame-bottom, 0px)' }"
    >
      <FeatureGate v-if="paywallFlag" :flag="paywallFlag">
        <slot />
      </FeatureGate>
      <slot v-else />
    </main>
  </div>
  <template v-else>
    <FeatureGate v-if="paywallFlag" :flag="paywallFlag">
      <slot />
    </FeatureGate>
    <slot v-else />
  </template>
</template>

<style>
/* One mechanism for every fixed shell bar: the easing makes them glide when a
   dock collapses in one jump, and a drag opts out. Without the opt-out a drag
   rewrites --sidepanel-width every frame, restarting the ease each frame so
   the bars trail the cursor. Sidepanel.vue clears the flag on mouseup, on
   window blur, on undock and on unmount. */
[data-action-bar],
[data-page-frame-header] {
  transition:
    left 300ms,
    right 300ms,
    top 300ms;
}
:root[data-dock-resizing] :is([data-action-bar], [data-page-frame-header]) {
  transition: none;
}

@media (min-width: 48rem) {
  :root:has([data-action-bar][data-has-page-title="true"])
    [data-page-frame-title],
  :root:has([data-action-bar][data-has-page-title="true"])
    [data-page-frame-control] {
    visibility: hidden;
  }
}
</style>
